import { Link } from 'react-router-dom';
import { IoChevronForward } from 'react-icons/io5';
import type { Order } from '@pelipost/consumer-api-client';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/components/ui/utils';

/**
 * One-line summary of an order, used on Home's Active Orders card and
 * (future) the full orders list page.
 *
 * Clickable: whole row links to /app/orders/:id (PEL-833 will add that
 * route; before then the link still renders and would 404 — acceptable
 * because the home card only shows orders the user has already placed,
 * and orders can't exist without that ticket shipping first).
 */

/** Format ISO cents into $12.50 style. */
function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

/** Human label for a status string (e.g. 'in_production' -> 'In production'). */
function humanizeStatus(status: string): string {
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Tone-map status strings to Badge variants. */
function statusTone(
  status: string,
): 'delivered' | 'inTransit' | 'processing' | 'returned' | 'outline' {
  if (status === 'delivered') return 'delivered';
  if (status === 'shipped' || status === 'in_transit') return 'inTransit';
  if (
    status === 'in_production' ||
    status === 'processing' ||
    status === 'pending' ||
    status === 'screening'
  ) {
    return 'processing';
  }
  if (status === 'rejected' || status === 'cancelled' || status === 'failed' || status === 'returned') {
    return 'returned';
  }
  return 'outline';
}

export interface OrderRowProps {
  order: Order;
  className?: string;
}

export function OrderRow({ order, className }: OrderRowProps) {
  return (
    <Link
      to={`/app/orders/${order.id}`}
      className={cn(
        'flex items-center gap-3 px-4 py-3 min-h-[64px]',
        'hover:bg-[var(--background)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
        'border-b border-[var(--border)] last:border-b-0',
        className,
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-[14px] text-[var(--text)] truncate">
            #{order.orderNumber}
          </p>
          <Badge variant={statusTone(order.status)} className="shrink-0">
            {humanizeStatus(order.status)}
          </Badge>
        </div>
        <p className="text-[12px] text-[var(--text-light)] truncate">
          {order.recipientName} · {order.facilityName}
        </p>
        <p className="text-[12px] text-[var(--text-muted)]">
          {order.itemCount} {order.itemCount === 1 ? 'item' : 'items'} · {formatUsd(order.totalCents)}
        </p>
      </div>
      <IoChevronForward size={18} className="text-[var(--text-muted)] shrink-0" aria-hidden />
    </Link>
  );
}
